/**
 * Web-audio chimes synthesized on the fly — no external assets, no MP3 download.
 * Browsers require a prior user gesture (the "Enable sound" toggle counts), so
 * pages should expose a button that calls `unlockAudio()` once.
 */

let _ctx = null;
let _unlocked = false;

function getCtx() {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  _ctx = new Ctor();
  return _ctx;
}

/** Call inside a user-gesture handler (button click) to unlock audio on iOS/Safari. */
export async function unlockAudio() {
  const ctx = getCtx();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    // Play a 1-sample silent buffer to fully unlock
    const buffer = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
    _unlocked = true;
    return true;
  } catch (e) {
    return false;
  }
}

/** Generic tone helper. */
function tone({ freq = 880, durationMs = 180, when = 0, gain = 0.18, type = 'sine' } = {}) {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  // Quick attack + smooth release to avoid clicks
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durationMs / 1000);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durationMs / 1000 + 0.02);
}

/** Two-tone kitchen "ding-ding" — bright + audible across a noisy kitchen. */
export function playKitchenChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  // First ping (G5)
  tone({ freq: 784,  durationMs: 220, when: 0,    gain: 0.22, type: 'triangle' });
  // Second, higher ping (C6) for that "you've got something" feel
  tone({ freq: 1046, durationMs: 260, when: 0.18, gain: 0.22, type: 'triangle' });
}

/** Softer single ding for admin / server console. */
export function playAdminPing() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  tone({ freq: 880, durationMs: 200, gain: 0.15, type: 'sine' });
}

/** Alarm — repeating ping for unmissable alerts (low stock, etc.). */
export function playAlarmChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  tone({ freq: 660, durationMs: 180, when: 0,    gain: 0.22, type: 'square' });
  tone({ freq: 660, durationMs: 180, when: 0.25, gain: 0.22, type: 'square' });
  tone({ freq: 660, durationMs: 180, when: 0.50, gain: 0.22, type: 'square' });
}

export function isAudioUnlocked() { return _unlocked; }
